import React from "react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Bell, Download, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useEditorContext } from "../../contexts/editor-context";
import { toast } from "sonner";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { WatermarkWarningModal } from "./watermark-warning-modal";
import { SubscriptionUpgradeModal } from "../../../../components/modals/SubscriptionUpgradeModal";

/**
 * Interface representing a single video render attempt
 * @property {string} url - URL of the rendered video (if successful)
 * @property {Date} timestamp - When the render was completed
 * @property {string} id - Unique identifier for the render
 * @property {'success' | 'error'} status - Result of the render attempt
 * @property {string} error - Error message if render failed
 */
interface RenderItem {
  url?: string;
  timestamp: Date;
  id: string;
  status: "success" | "error";
  error?: string;
}

/**
 * Props for the RenderControls component
 * @property {object} state - Current render state containing status, progress, and URL
 * @property {() => void} handleRender - Function to trigger a new render
 */
interface RenderControlsProps {
  state: any;
  handleRender: () => void;
}

/**
 * RenderControls component provides UI controls for video rendering functionality
 *
 * Features:
 * - Render button that shows progress during rendering
 * - Notification bell showing render history
 * - Download buttons for completed renders
 * - Error display for failed renders
 *
 * The component maintains a history of render attempts, both successful and failed,
 * and provides visual feedback about the current render status.
 */
const RenderControls: React.FC<RenderControlsProps> = ({
  state,
  handleRender,
}) => {
  // Get render type from editor context
  const { renderType } = useEditorContext();

  // Check if rendering is enabled via environment variable
  const isRenderingEnabled = import.meta.env.VITE_RENDERING_ENABLED === "true";

  // Query current user to check subscription status
  const currentUser = useQuery(api.users.getCurrentUser);
  const isFreeUser = !currentUser?.profile?.role || currentUser?.profile?.role === "free";

  // Store multiple renders
  const [renders, setRenders] = React.useState<RenderItem[]>([]);
  // Track if there are new renders
  const [hasNewRender, setHasNewRender] = React.useState(false);
  // Track which render is currently downloading
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  // Watermark warning modal state
  const [showWatermarkWarning, setShowWatermarkWarning] = React.useState(false);
  // Subscription upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);

  // Add new render to the list when completed
  React.useEffect(() => {
    if (state.status === "done") {
      setRenders((prev) => [
        {
          url: state.url,
          timestamp: new Date(),
          id: crypto.randomUUID(),
          status: "success",
        },
        ...prev,
      ]);
      setHasNewRender(true);
    } else if (state.status === "error") {
      setRenders((prev) => [
        {
          timestamp: new Date(),
          id: crypto.randomUUID(),
          status: "error",
          error:
            state.error?.message || "Failed to render video. Please try again.",
        },
        ...prev,
      ]);
      setHasNewRender(true);
    }
  }, [state.status, state.url, state.error]);

  const handleDownload = async (url: string, renderId: string) => {
    // Prevent multiple simultaneous downloads
    if (downloadingId) return;

    setDownloadingId(renderId);
    let downloadUrl = url;

    // Check if it's a Convex URL (full https URL)
    const isConvexUrl = url.startsWith("https://");

    if (isConvexUrl) {
      // Convex URLs are used directly
      downloadUrl = url;
    } else if (renderType === "ssr") {
      // Convert the video URL to a download URL for SSR (local files)
      downloadUrl = url
        .replace("/rendered-videos/", "/api/latest/ssr/download/")
        .replace(".mp4", "");
    }
    // Lambda URLs are already in the correct format for download

    try {
      // Fetch the video as a blob to force download instead of opening in browser
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to fetch video");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "rendered-video.mp4";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL after a short delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed with blob method, trying direct download:", error);

      // Fallback: try direct anchor download without opening new window
      // This works for same-origin URLs and some cross-origin URLs with proper headers
      try {
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "rendered-video.mp4";
        // Don't use target="_blank" - it triggers popup blockers
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        toast.success("Download started!");
      } catch (fallbackError) {
        console.error("Direct download also failed:", fallbackError);
        toast.error("Download failed. Please try again or right-click the link to save.", {
          duration: 5000,
        });
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const getDisplayFileName = (url: string) => {
    // Check if it's a Convex URL (full https URL)
    if (url.startsWith("https://")) {
      // For Convex/Lambda URLs, extract filename from pathname
      try {
        const pathname = new URL(url).pathname;
        return pathname.split("/").pop() || "rendered-video.mp4";
      } catch {
        return "rendered-video.mp4";
      }
    }
    // For local SSR URLs
    return url.split("/").pop();
  };

  // Handle render button click - show warning for free users
  const handleRenderClick = () => {
    if (isFreeUser) {
      setShowWatermarkWarning(true);
    } else {
      handleRender();
    }
  };

  // Handle continue with watermark from the warning modal
  const handleContinueWithWatermark = () => {
    setShowWatermarkWarning(false);
    handleRender();
  };

  // Handle upgrade click from the warning modal
  const handleUpgradeClick = () => {
    setShowWatermarkWarning(false);
    setShowUpgradeModal(true);
  };

  return (
    <>
      <Popover onOpenChange={() => setHasNewRender(false)}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="relative hover:bg-accent text-foreground"
          >
            <Bell className="w-3.5 h-3.5" />
            {hasNewRender && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-3">
          <div className="space-y-1.5">
            <h4 className="text-sm font-extralight">Recent Renders</h4>
            {renders.length === 0 ? (
              <p className="text-xs text-muted-foreground">No renders yet</p>
            ) : (
              renders.map((render) => (
                <div
                  key={render.id}
                  className={`flex items-center justify-between rounded-md border p-1.5 ${
                    render.status === "error"
                      ? "border-destructive/50 bg-destructive/10"
                      : "border-border"
                  }`}
                >
                  <div className="flex flex-col">
                    <div className="text-xs text-primary">
                      {render.status === "error" ? (
                        <span className="text-red-400 font-extralight">
                          Render Failed
                        </span>
                      ) : (
                        getDisplayFileName(render.url!)
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {formatDistanceToNow(render.timestamp, {
                        addSuffix: true,
                      })}
                      {render.error && (
                        <div
                          className="text-red-400 mt-0.5 truncate max-w-[180px]"
                          title={render.error}
                        >
                          {render.error}
                        </div>
                      )}
                    </div>
                  </div>
                  {render.status === "success" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-foreground hover:bg-accent h-6 w-6"
                      onClick={() => handleDownload(render.url!, render.id)}
                      disabled={downloadingId === render.id}
                    >
                      {downloadingId === render.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Button
        onClick={handleRenderClick}
        size="sm"
        variant="outline"
        disabled={state.status === "rendering" || state.status === "invoking" || state.status === "uploading" || !isRenderingEnabled}
        className={`bg-gray-800 text-white border-gray-700 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 ${!isRenderingEnabled ? "cursor-not-allowed" : ""}`}
        title={!isRenderingEnabled ? "Rendering is currently disabled" : undefined}
      >
        {!isRenderingEnabled ? (
          "Render Video"
        ) : state.status === "uploading" ? (
          <>
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            Uploading...
          </>
        ) : state.status === "rendering" ? (
          <>
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            Rendering... {(state.progress * 100).toFixed(0)}%
          </>
        ) : state.status === "invoking" ? (
          <>
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            Preparing...
          </>
        ) : (
          `Render Video`
        )}
      </Button>

      {/* Watermark Warning Modal for free users */}
      <WatermarkWarningModal
        isOpen={showWatermarkWarning}
        onClose={() => setShowWatermarkWarning(false)}
        onContinue={handleContinueWithWatermark}
        onUpgrade={handleUpgradeClick}
      />

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
};

export default RenderControls;
